import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MoreHorizontal, Pencil, Trash2, Users } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { showDate } from '../../shared/tree'
import { ActionMenu } from '@/components/ActionMenu'
import { FormError } from '@/components/FormError'
import { Byline } from '@/components/MomentCard'
import { OriginalPhoto } from '@/components/OriginalPhoto'
import { ShareWith } from '@/components/ShareWith'
import { Avatar } from '@/components/tree/Avatar'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { photoSrcSet } from '@/lib/albums'
import { api } from '@/lib/api'
import { useFamily, type Family } from '@/lib/families'
import { useItemHome, usePageFrame } from '@/lib/frame'
import { audience, isPrivate, momentPhotoUrl, paragraphs, useMoment, type Moment } from '@/lib/moments'
import { audienceBody, audienceFrom } from '@/lib/spaces'
import { tint } from '@/lib/paper'
import { cn } from '@/lib/utils'
import { BigWords, MoreMoments, PhotoCarousel } from '@/components/modern-moments/MomentPost'
import { ToldLabel } from '@/components/modern-moments/Told'
import { shapeOf } from '../../shared/momentStyle'
import { useModernMoments } from '@/components/modern-moments/useModern'
import { PageLoader } from '@/components/Loader'

export function MomentPage() {
  const { id = '' } = useParams()
  const moment = useMoment(id)
  if (moment.isPending) return <PageLoader />
  if (moment.isError) return <FormError error={moment.error} />
  return <MomentView moment={moment.data} />
}

// One moment, whole: who wrote it and who sees it, when it happened, its photos, the story and who was there. Its
// author can change it, share it or stop sharing it, or delete it. There is nothing to like.
function MomentView({ moment }: { moment: Moment }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  // Back to the moments of the place it was opened from, which may be another circle it's shared into, or Everyone.
  const home = useItemHome(moment)
  const listTo = `/circle/${home}/moments`
  usePageFrame(home, { title: moment.title, back: { to: listTo, label: 'Back to moments' } })
  // The circle's spaces, for sharing it somewhere else; only its author needs them.
  const family = useFamily(moment.familyId, { enabled: moment.mine })
  const [sharing, setSharing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  // Its author can look back at the story exactly as they wrote it, when it's shown polished or retold.
  const [asWritten, setAsWritten] = useState(false)
  const remove = useMutation({
    mutationFn: () => api(`/moments/${moment.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['moments', moment.familyId] })
      navigate(listTo, { replace: true })
    },
  })
  const story = paragraphs(asWritten && moment.written !== undefined ? moment.written : moment.body)
  const firstName = moment.author.name.split(/\s+/)[0]
  // In Modern style it reads as a post, drawn the way its author chose: the photos first, a few words set large, or
  // the story first; then what it leads to.
  const modern = useModernMoments()
  const shape = shapeOf({ ...moment, photoCount: moment.photos.length })
  const menu = moment.mine && (
    <ActionMenu
      title={`Options for ${moment.title}`}
      align="end"
      trigger={
        <Button variant="ghost" size="icon" className="-mr-2 shrink-0" aria-label="Moment options">
          <MoreHorizontal aria-hidden />
        </Button>
      }
      groups={[
        [
          { label: 'Edit', icon: Pencil, onSelect: () => navigate(`/moment/${moment.id}/edit`) },
          { label: 'Who sees it', icon: Users, onSelect: () => setSharing(true) },
        ],
        [{ label: 'Delete moment', icon: Trash2, onSelect: () => setDeleting(true) }],
      ]}
    />
  )

  return (
    <article aria-labelledby="moment-title" className="flex flex-col gap-8">
      {modern ? (
        <ModernTop moment={moment} shape={shape} menu={menu} listTo={listTo} />
      ) : (
        <>
          <header className="flex flex-col gap-5">
            <Link to={listTo} className="self-start eyebrow hover:underline max-nav:hidden">
              ← Moments
            </Link>
            <div className="flex items-start gap-3">
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                {moment.happenedOn && <p className="eyebrow">{showDate(moment.happenedOn)}</p>}
                <h1 id="moment-title" className="text-display text-balance">
                  {moment.title}
                </h1>
              </div>
              {menu}
            </div>
            <Byline moment={moment} whole />
          </header>

          {moment.photos.length > 0 && (
            <ul className={cn('grid gap-2', moment.photos.length > 1 && 'grid-cols-2')}>
              {moment.photos.map((photo, i) => {
                const name = `Photo ${i + 1} of ${moment.photos.length} in ${moment.title}`
                // The first photo leads, across the page; the rest sit two to a row.
                const lead = i === 0 && moment.photos.length !== 2
                return (
                  <li key={photo.id} className={cn(lead && moment.photos.length > 1 && 'col-span-2')}>
                    <OriginalPhoto
                      src={momentPhotoUrl(photo.id, 'original')}
                      fallback={momentPhotoUrl(photo.id, 'display')}
                      alt={name}
                    >
                      <button
                        type="button"
                        aria-label={`Open ${name.charAt(0).toLowerCase()}${name.slice(1)}`}
                        className={cn(
                          'block w-full overflow-hidden rounded-2xl outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60',
                          tint(i),
                        )}
                      >
                        <img
                          src={momentPhotoUrl(photo.id, 'medium')}
                          srcSet={photoSrcSet(
                            { ...photo, ref: photo.id },
                            ['thumb', 'medium', 'display'],
                            momentPhotoUrl,
                          )}
                          sizes={lead ? '(min-width: 768px) 44rem, 100vw' : '(min-width: 768px) 22rem, 50vw'}
                          width={photo.width}
                          height={photo.height}
                          alt=""
                          decoding="async"
                          className={cn('w-full object-cover', lead ? 'max-h-[75svh]' : 'aspect-square')}
                        />
                      </button>
                    </OriginalPhoto>
                  </li>
                )
              })}
            </ul>
          )}
        </>
      )}

      {story.length > 0 && !(modern && shape === 'words' && moment.photos.length === 0) && (
        <div className="flex flex-col gap-4 text-body leading-relaxed">
          {moment.written !== undefined && (
            <p className="flex flex-wrap items-baseline gap-x-2 text-sm text-muted-foreground">
              <span>
                {asWritten
                  ? 'What you wrote. Only you see this.'
                  : moment.toldAs === 'retold'
                    ? 'Retold from your words.'
                    : 'Polished from your words.'}
              </span>
              <button
                type="button"
                aria-pressed={asWritten}
                className="font-medium text-brand underline underline-offset-4"
                onClick={() => setAsWritten((x) => !x)}
              >
                {asWritten ? 'Show how others read it' : 'Show what I wrote'}
              </button>
            </p>
          )}
          {story.map((p, i) => (
            <p key={i} className="whitespace-pre-line">
              {p}
            </p>
          ))}
          {!moment.mine && moment.toldAs === 'retold' && (
            <p className="text-sm text-muted-foreground">Retold from {firstName}’s own words.</p>
          )}
        </div>
      )}

      {moment.people.length > 0 && (
        <section className="flex flex-col gap-3" aria-labelledby="moment-people">
          <h2 id="moment-people" className="eyebrow">
            Who was there
          </h2>
          <ul className="flex flex-wrap gap-2">
            {moment.people.map((p) => (
              <li key={p.id}>
                <Link
                  to={`/person/${p.id}`}
                  className="flex min-h-10 items-center gap-2 rounded-full bg-secondary py-1 pr-4 pl-1 text-sm font-medium outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                  <Avatar person={p} className="size-8 text-sm shadow-none" />
                  {p.name}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {modern && <MoreMoments moment={moment} />}

      {moment.mine && isPrivate(moment) && (
        <p className="rounded-2xl bg-secondary px-4 py-3 text-sm text-muted-foreground">
          Only you can see this moment.{' '}
          <button
            type="button"
            className="font-medium text-brand underline underline-offset-4"
            onClick={() => setSharing(true)}
          >
            Share it
          </button>{' '}
          when you’re ready.
        </p>
      )}

      {family.data && (
        <ShareDialog
          key={String(sharing)}
          open={sharing}
          onOpenChange={setSharing}
          moment={moment}
          family={family.data}
        />
      )}
      <AlertDialog open={deleting} onOpenChange={setDeleting}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this moment?</AlertDialogTitle>
            <AlertDialogDescription>
              “{moment.title}” and its photos will be gone for everyone who can see it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <FormError error={remove.error} />
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                remove.mutate()
              }}
              disabled={remove.isPending}
            >
              Delete moment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </article>
  )
}

// Who sees a moment, changed in one place: kept just for its author, their household, or everyone in the circle.
function ShareDialog({
  open,
  onOpenChange,
  moment,
  family,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  moment: Moment
  family: Family
}) {
  const queryClient = useQueryClient()
  const [who, setWho] = useState(() => audienceFrom(moment))
  const save = useMutation({
    mutationFn: () => api(`/moments/${moment.id}`, { method: 'PATCH', body: audienceBody(who) }),
    onSuccess: async () => {
      // It may now show in other circles too.
      for (const key of ['moment', 'moments', 'home']) await queryClient.invalidateQueries({ queryKey: [key] })
      onOpenChange(false)
    },
  })
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Who sees it?</DialogTitle>
          <DialogDescription>
            Only the people you pick will see this moment. You can change it any time.
          </DialogDescription>
        </DialogHeader>
        <ShareWith
          here={family.id}
          value={who}
          onChange={setWho}
          onlyMeHint="Kept just for you. Share it whenever you’re ready."
        />
        <FormError error={save.error} />
        <Button size="lg" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogContent>
    </Dialog>
  )
}

// The top of a moment in Modern style: who wrote it and when, then its photos and title in the order its shape asks
// for. A Words moment sets its story large in place of the photos.
function ModernTop({
  moment,
  shape,
  menu,
  listTo,
}: {
  moment: Moment
  shape: ReturnType<typeof shapeOf>
  menu: React.ReactNode
  listTo: string
}) {
  const title = (
    <h1
      id="moment-title"
      className={cn('text-balance', shape === 'words' ? 'text-title font-semibold' : 'text-display')}
    >
      {moment.title}
    </h1>
  )
  const photos = moment.photos.length > 0 && <PhotoCarousel photos={moment.photos} title={moment.title} />
  return (
    <>
      <Link to={listTo} className="self-start eyebrow hover:underline max-nav:hidden">
        ← Moments
      </Link>
      <header className="flex items-center gap-3">
        <Avatar person={moment.author} className="size-10 text-sm shadow-none" />
        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="truncate font-semibold">{moment.author.name}</span>
          <ToldLabel moment={{ ...moment, photoCount: moment.photos.length }} />
        </div>
        <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-muted-foreground">
          {audience(moment, true)}
        </span>
        {menu}
      </header>
      {shape === 'words' ? (
        <>
          <BigWords words={moment.body.trim() || moment.title} />
          {title}
          {photos}
        </>
      ) : shape === 'story' ? (
        <>
          {title}
          {photos}
        </>
      ) : (
        <>
          {photos}
          {title}
        </>
      )}
    </>
  )
}
